<template>
  <AdminDashboard id="app" :currentLanguage="currentLocale" :currentRoute="currentRoute">
    <template #headerMetaNavigation>
      <ProfileMenu :menuItem="loggedInMenuItem" :currentRoute="currentRoute" />
    </template>
    <template #sidebarMainNavigation>
      <SidebarMenu :menuItems="sideBarMenuItems" :currentRoute="currentRoute" />
    </template>
    <template #default>
      <RouterView />
      <HgToast :isVisible="toast.isOpen || false" @clear="onHideToast">
        <HgNotificationMessage
          :data-cy="`toast-message-${toast.theme}`"
          :title="toast.title"
          :theme="toast.theme"
          showCloseButton
          @close="onHideToast"
          >{{ toast.message }}
        </HgNotificationMessage>
      </HgToast>
    </template>
  </AdminDashboard>
</template>

<script lang="ts" setup>
import AdminDashboard from '@/layouts/Dashboard/AdminDashboard.vue';
import { HgNotificationMessage, HgSupportedLanguage, HgToast, Icon } from '@smg-real-estate/domus';
import { RouterView, useRoute } from 'vue-router';

import { Roles } from '@/components/Agency/UserFormData/types';
import { initHotjar } from '@/helpers/Hotjar/hotjar';

import { flatfoxLeadsUrl } from '@/helpers/flatfoxUtils';
import ProfileMenu from '@/layouts/Dashboard/ProfileMenu.vue';
import SidebarMenu from '@/layouts/Dashboard/SidebarMenu.vue';
import type { DropdownMenuItem, ISidebarMenuItem } from '@/layouts/types';

import { isFeatureEnabled, SupportedFeatures } from '@/helpers/featureFlagUtils';
import { hasAccessToProspectionMap } from '@/helpers/hasAccessToProspectionMap';
import { hasVisibilityLightAccess } from '@/helpers/hasVisibilityLightAccess';
import routes from '@/router/routes/routeNames';
import { ToastTheme, type Toast } from '@/store/app/AppStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { SalesforceHgInventoryFocusGroupEnum } from '@/types-agency';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { useAgencyStore } from '@/stores/agency/useAgencyStore';

enum SidebarMenuItemIdentifier {
  LISTINGS_GROUP = 'listingGroup',
  LISTINGS = 'listings',
  NEW_LISTING = 'newListing',
  CRM_UPLOAD_REPORT = 'crmUploadReport',
  FLATFOX_LEADS = 'flatfoxLeads',
  INSIGHTS_HUB_GROUP = 'insightsHubGroup',
  LISTING_INSIGHTS = 'listingInsights',
  WHATS_NEW = 'whatsNew',
  AGENCY_GROUP = 'agencyGroup',
  AGENCY = 'agency',
  LOGOUT = 'logout',
  TEAM_MEMBER = 'teamMember',
  AGENCY_PROFILE = 'agencyProfile',
  CONTACT_LIST = 'contactList',
  REFERENCE_LISTINGS = 'referenceListings',
  REGIONAL_HIGHLIGHT = 'regionalHighlight',
  COST_REPORT = 'costReport',
  NEWS = 'news',
  TOOLS_GROUP = 'toolsGroup',
  PROSPECTION_MAP = 'prospectionMap',
  PARTNERS = 'partners',
  MANDATE_ACQUISITION = 'mandateAcquisition',
  WINNING_MANDATE = 'winningMandate',
  VISIBILITY_PLUS = 'visibilityPlus',
  PACKAGE_OVERVIEW = 'packageOverview',
  INVOICES = 'invoices',
}

const filterMenuItemsByUserGroups = (
  menuItems: ISidebarMenuItem[],
  userRole: Roles,
): ISidebarMenuItem[] => {
  return menuItems.filter((menuItem) => {
    if (Number(menuItem?.submenuItems?.length) > 0) {
      menuItem.submenuItems = filterMenuItemsByUserGroups(
        menuItem?.submenuItems ?? ([] as ISidebarMenuItem[]),
        userRole,
      );
    }

    return menuItem.allowedForRoles.includes(userRole);
  });
};

useHead({
  title: 'Unified Business Manager',
});

const store = useStore();
const userStore = useUserStore();
const agencyStore = useAgencyStore();
const {
  agencies,
  activeAgencyId,
  activeAgency,
  activeProducts,
  isBillToAgency,
  isFlatfoxActive,
  hasImportData,
  isBronzePackage,
  isSilverPackage,
  isGoldPackage,
  isWelcomePackage,
  flatfoxAgencyId,
} = storeToRefs(agencyStore);

const loggedInMenuItemLabel = computed<string>(() => userStore.logonId);

const { locale, t } = useI18n();

const currentLocale = computed<string>(() => locale.value);

const currentRoute = useRoute();

const hasUserAgencies = computed(() => agencies.value.length > 0);

const hasActiveAgency = computed(() => Boolean(activeAgencyId.value));

const isMandateAcquisitionActive = computed<boolean>(
  () => activeAgency.value?.mandateAcquisitionActive ?? false,
);
const hasAgencyLeads = computed<boolean>(() => store.getters.getHasAgencyLeads ?? false);
const shouldShowSellerLeads = computed<boolean>(
  () => isMandateAcquisitionActive.value || hasAgencyLeads.value,
);
const isProspectionMapActive = computed<boolean>(() =>
  hasAccessToProspectionMap(activeProducts.value),
);

const isPremiumAgency = computed<boolean>(() => activeAgency.value?.isPremiumAgency ?? false);

const agencyProfile = computed(() => store.getters['agencyProfile/getAgencyProfile']);

const isAgencyInBuyDomain = computed<boolean>(() => {
  const focusGroup = agencyProfile.value?.hgInventoryFocusGroup;
  return (
    focusGroup === SalesforceHgInventoryFocusGroupEnum.MixedFocus ||
    focusGroup === SalesforceHgInventoryFocusGroupEnum.Sale
  );
});

const isVisibilityLight = computed<boolean>(() => hasVisibilityLightAccess(activeProducts.value));

const isPackageOverviewAccessible = computed<boolean>(() => {
  return isBronzePackage.value || isSilverPackage.value || isGoldPackage.value;
});

const isWinningMandateEnabled = computed<boolean>(() =>
  isFeatureEnabled(SupportedFeatures.ENABLE_WINNING_MANDATE, currentRoute),
);

const isRhSlotManagementEnabled = computed<boolean>(() =>
  isFeatureEnabled(SupportedFeatures.RH_SLOT_MANAGEMENT, currentRoute),
);

const hasReferenceListingsAccess = computed<boolean>(() => {
  return (
    isPremiumAgency.value || isGoldPackage.value || isSilverPackage.value || isWelcomePackage.value
  );
});

const getFlatfoxLeadsUrl = computed<string>(() => {
  return flatfoxLeadsUrl(
    flatfoxAgencyId.value,
    currentRoute.params?.locale?.toString() as HgSupportedLanguage,
  );
});

const agencyGroupSubmenuItems = computed<ISidebarMenuItem[]>(() => [
  {
    id: SidebarMenuItemIdentifier.TEAM_MEMBER,
    label: t('adminDashboard.sidebar.menu.teamMembers'),
    named: {
      name: routes.userManagement.userManagement,
    },
    allowedForRoles: [Roles.Admin],
  },
  {
    id: SidebarMenuItemIdentifier.AGENCY_PROFILE,
    label: t('adminDashboard.sidebar.menu.agencyProfilePage'),
    named: {
      name: routes.agencyProfile,
    },
    allowedForRoles: [Roles.Admin, Roles.Editor],
  },
  {
    id: SidebarMenuItemIdentifier.CONTACT_LIST,
    label: t('adminDashboard.sidebar.menu.teamPage'),
    named: {
      name: routes.contactList,
    },
    allowedForRoles: [Roles.Admin, Roles.Editor],
  },
  ...(hasReferenceListingsAccess.value || isVisibilityLight.value
    ? [
        {
          id: SidebarMenuItemIdentifier.REFERENCE_LISTINGS,
          label: t('adminDashboard.sidebar.menu.referenceListings'),
          named: {
            name: routes.referenceListings,
          },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
      ]
    : []),
  ...(isRhSlotManagementEnabled.value
    ? [
        {
          id: SidebarMenuItemIdentifier.REGIONAL_HIGHLIGHT,
          label: t('adminDashboard.sidebar.menu.regionalHighlight'),
          named: {
            name: routes.regionalHighlight,
            // The item only renders while the flag is in the URL, so forwarding the
            // current value keeps this route reachable without dropping other flags.
            query: { feature: currentRoute.query.feature },
          },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
      ]
    : []),
  ...(isPremiumAgency.value || isVisibilityLight.value || isAgencyInBuyDomain.value
    ? [
        {
          id: SidebarMenuItemIdentifier.VISIBILITY_PLUS,
          label: t('adminDashboard.sidebar.menu.visibilityPlus'),
          named: {
            name: routes.visibilityPlus,
          },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
      ]
    : []),
  {
    id: SidebarMenuItemIdentifier.COST_REPORT,
    label: isBillToAgency.value
      ? t('adminDashboard.sidebar.menu.billingReport')
      : t('adminDashboard.sidebar.menu.costReport'),

    named: {
      name: routes.costReport,
    },
    allowedForRoles: [Roles.Admin, Roles.Editor],
  },
  ...(isPackageOverviewAccessible.value
    ? [
        {
          id: SidebarMenuItemIdentifier.PACKAGE_OVERVIEW,
          label: t('agency.packageOverview.navItem'),
          named: {
            name: routes.packageOverview,
          },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
      ]
    : []),
  {
    id: SidebarMenuItemIdentifier.INVOICES,
    label: t('agency.invoices.title'),
    named: {
      name: routes.invoices,
    },
    allowedForRoles: [Roles.Admin],
  },
]);

const sideBarMenuItems = computed<ISidebarMenuItem[]>(() => {
  const hasUserCCCGroup = userStore.hasCCCGroup;

  const menuItems: ISidebarMenuItem[] = [
    {
      id: SidebarMenuItemIdentifier.LISTINGS_GROUP,
      label: t('adminDashboard.sidebar.menu.listingsGroup'),
      allowedForRoles: [Roles.Admin, Roles.Editor],
      icon: Icon.Clipboard,
      submenuItems: [
        {
          id: SidebarMenuItemIdentifier.LISTINGS,
          label: t('adminDashboard.sidebar.menu.listings'),
          named: {
            name: 'business-listings-manage',
          },
          subpagePath: '/insights',
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
        {
          id: SidebarMenuItemIdentifier.NEW_LISTING,
          label: t('adminDashboard.sidebar.menu.newListing'),
          named: {
            name: 'business-listings-new',
          },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
        ...(hasImportData.value
          ? [
              {
                id: SidebarMenuItemIdentifier.CRM_UPLOAD_REPORT,
                label: t('importReportPage.title'),
                named: {
                  name: routes.crmUploadReport,
                },
                allowedForRoles: [Roles.Admin, Roles.Editor],
              },
            ]
          : []),
        ...(isFlatfoxActive.value
          ? [
              {
                id: SidebarMenuItemIdentifier.FLATFOX_LEADS,
                label: 'Flatfox lead management',
                href: getFlatfoxLeadsUrl.value,
                external: true,
                allowedForRoles: [Roles.Admin, Roles.Editor],
              },
            ]
          : []),
      ],
    },
    {
      id: SidebarMenuItemIdentifier.INSIGHTS_HUB_GROUP,
      label: t('adminDashboard.sidebar.menu.insightsHubGroup'),
      allowedForRoles: [Roles.Admin, Roles.Editor],
      icon: Icon.Uplift,
      submenuItems: [
        {
          id: SidebarMenuItemIdentifier.LISTING_INSIGHTS,
          label: t('adminDashboard.sidebar.menu.listingInsights'),
          named: { name: 'listing-insights' },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
        {
          id: SidebarMenuItemIdentifier.WHATS_NEW,
          label: t('adminDashboard.sidebar.menu.whatsNew'),
          named: { name: routes.whatsNew },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
      ],
    },
    {
      id: SidebarMenuItemIdentifier.AGENCY_GROUP,
      label: t('adminDashboard.sidebar.menu.agencyGroup'),
      allowedForRoles: [Roles.Admin, Roles.Editor],
      icon: Icon.Apartment,
      submenuItems: agencyGroupSubmenuItems.value,
    },
    {
      id: SidebarMenuItemIdentifier.TOOLS_GROUP,
      label: t('adminDashboard.sidebar.menu.toolsGroup'),
      allowedForRoles: [Roles.Admin, Roles.Editor],
      icon: Icon.Suitcase,
      submenuItems: [
        {
          id: SidebarMenuItemIdentifier.PARTNERS,
          label: t('adminDashboard.sidebar.menu.ecosystem'),
          named: {
            name: routes.ecosystem,
          },
          allowedForRoles: [Roles.Admin, Roles.Editor],
        },
        ...(isProspectionMapActive.value
          ? [
              {
                id: SidebarMenuItemIdentifier.PROSPECTION_MAP,
                label: t('adminDashboard.sidebar.menu.prospectionMap'),
                named: {
                  name: routes.prospectionMap,
                },
                allowedForRoles: [Roles.Admin, Roles.Editor],
              },
            ]
          : []),
        ...(shouldShowSellerLeads.value
          ? [
              {
                id: SidebarMenuItemIdentifier.MANDATE_ACQUISITION,
                label: t('adminDashboard.sidebar.menu.mandateAcquisition'),
                named: {
                  name: routes.sellerLeads,
                },
                allowedForRoles: [Roles.Admin, Roles.Editor],
              },
            ]
          : []),
        ...(isWinningMandateEnabled.value
          ? [
              {
                id: SidebarMenuItemIdentifier.WINNING_MANDATE,
                label: t('adminDashboard.sidebar.menu.winningMandates'),
                named: {
                  name: routes.winningMandate,
                },
                allowedForRoles: [Roles.Admin, Roles.Editor],
              },
            ]
          : []),
      ],
    },
    {
      id: SidebarMenuItemIdentifier.NEWS,
      label: t('adminDashboard.sidebar.menu.news'),
      href: t('adminDashboard.sidebar.externalLink.news'),
      icon: Icon.Description,
      external: true,
      allowedForRoles: [Roles.Admin, Roles.Editor],
    },
  ];

  if (hasUserCCCGroup && hasUserAgencies.value) {
    return menuItems;
  } else if (hasUserCCCGroup) {
    // If the user is not assigned to any agency, we only show the news link
    return menuItems.filter((item) => item.id === SidebarMenuItemIdentifier.NEWS);
  } else {
    const { role: userRole } = activeAgency.value || {};

    return filterMenuItemsByUserGroups(menuItems, userRole as Roles);
  }
});

const loggedInMenuItem = computed<DropdownMenuItem>(() => {
  return {
    label: loggedInMenuItemLabel.value,
    children: [
      {
        label: t('userProfile.title'),
        to: `/${currentLocale.value}/ubm/profile`,
      },
      {
        label: t('adminDashboard.navigation.logout'),
        to: `/${currentLocale.value}/ubm/logout`,
      },
    ],
  };
});

const toast = computed<Toast>(() => store.getters.getToast);

watch(
  () => store.state.funnelApi.error,
  (error) => {
    if (!error) return;

    store.dispatch('showToast', {
      title: t('application.error.title'),
      message: t('application.error.description'),
      theme: ToastTheme.Danger,
    });
  },
  { immediate: true },
);

onMounted(() => {
  window.location.protocol === 'https:' && initHotjar();
  if (activeAgencyId.value) {
    store.dispatch('fetchAgencyLeads');
  }
});

const onHideToast = (): void => {
  store.dispatch('hideToast');
};
</script>
